import angular from 'angular';
import csmCommonWeb from 'csm-common-web';

// TODO PK - make sure this isn't broken
// angular.module('books.ui.toggleButtons', ['csm.util.keycodes'])
// replaced module as keycodes lives within CommonWeb
export default angular
  .module('books.ui.toggleButtons', [csmCommonWeb])
  .directive('toggleButtons', toggleButtonsDirective).name;

/*******************************************************************************
 * toggleButtons */
/**
 * This is a wrapper for a group of buttons, using the bootstrap btn-group class.
 *
 * Buttons are supplied through the buttons attribute as an array of objects with
 * at least a property named "label". Other properties that can be set include a
 * "value" property (used to set a button programmatically through the
 * selected-value attribute. The "buttonColor" property will be used to set
 * individual button colors, and the "selectedButtonColor" will do the same when
 * that button has been selected.
 *
 * Other optional attributes include "buttonBorderColor" to customize the border
 * color between buttons, "multiSelect" to allow buttons to be toggled instead of
 * switched, as well as "vertical" to display the buttons in a vertical
 * orientation.
 *
 * @ngdoc     directive
 * @memberof  books.ui.toggleButtons
 * @name      books.ui.toggleButtons.directive:toggleButtons
 *
 * @element
 * @attribute
 *
 * @scope
 * @property  [buttonBorderColor='white']  {string}      Color of border between buttons.
 * @property  buttonChanged                {expression}  Evaluated when a button has been selected.
 *   $oldButton = last button that was selected
 *   $newButton = newly selected button.
 * @property  buttons                      {binding}     Array of button objects.
 *   label                                 Button label.
 *   [buttonColor         = "buttonface"]  Background color of button, defaults to "buttonface" (the default color for the btn class).
 *   [selectedButtonColor = "#5bc0de"]     Background color of button when selected, defaults to a blue color.
 *   [selectedTextColor   = "white"]       Text color when selected.
 *   [textColor           = "black"]       Text color.
 *   [value               = index]         Value when selected, defaults to button index.
 * @property  [multiSelect=false]          {expression}  Boolean value to allow multiple buttons to be selected.
 * @property  [vertical=false]             {expression}  Boolean value to orient the buttons vertically.
 *******************************************************************************/
/* @ngInject */
function toggleButtonsDirective(KEYCODES) {
  /*****************************************************************************
   * Constants
   *****************************************************************************/
  /** Button group template. */
  var HTML_BUTTON_TEMPLATE =
    '\
    <div class      = "toggle-buttons-{{$id}}"\
         ng-class   = "{\'btn-group\':!vertical(), \'btn-group-vertical\':vertical()}"\
         tabindex   = "0"\
         ng-keydown = "navigate($event)">\
      <button ng-repeat   = "btn in buttons"\
              ng-disabled = "disabled"\
              tabindex    = "-1"\
              type        = "button"\
              class       = "btn"\
              ng-class    = "{\'btn-info\' : btn.selected}"\
              ng-click    = "select(btn)"\
              ng-style    = "\
                {\
                \'background-color\':btn.selected ? btn.selectedColor     || constants.BTN_SELECTED_COLOR : btn.color     || constants.BTN_COLOR,\
                \'color\':           btn.selected ? btn.selectedTextColor || constants.TXT_SELECTED_COLOR : btn.textColor || constants.TXT_COLOR\
                }">\
        {{btn.label}}\
      </button>\
    </div>';

  var CSS_BUTTON_BORDERS =
    '\
    .toggle-buttons-{{$id}}.btn-group-vertical > .btn:not(:last-child)  { border-bottom-color: {{buttonBorderColor}} }\
    .toggle-buttons-{{$id}}.btn-group-vertical > .btn:not(:first-child) { border-top-color:    {{buttonBorderColor}} }\
    .toggle-buttons-{{$id}}.btn-group          > .btn:not(:last-child)  { border-right-color:  {{buttonBorderColor}} }\
    .toggle-buttons-{{$id}}.btn-group          > .btn:not(:first-child) { border-left-color:   {{buttonBorderColor}} }';

  /*****************************************************************************
   * ToggleButtons */
  /**
   * Link controller constructor. Sets up the navigation and select functions.
   *****************************************************************************/
  /* @ngInject */ function ToggleButtons($attrs, $compile, $element, $scope) {
    /***************************************************************************
     * Constants
     ***************************************************************************/
    /** Navigation keys. */
    var NAV_KEYS = [
      KEYCODES.LEFT_ARROW,
      KEYCODES.RIGHT_ARROW,
      KEYCODES.UP_ARROW,
      KEYCODES.DOWN_ARROW
    ];

    /***************************************************************************
     * Properties
     ***************************************************************************/
    /** Controller Instance. */ var mCtrl = this;
    /** Selected index for navigation. */ var selectedIndex = 0;
    /** ngModel Controller. */ var mNgModelCtrl = null;

    /*---------------------------*/
    /* Set controller functions. */
    /*---------------------------*/
    mCtrl.init = init;
    $scope.buttonBorderColor = $scope.buttonBorderColor || 'white';
    $scope.constants = {
      BTN_SELECTED_COLOR: '#5bc0de',
      BTN_COLOR: 'buttonface',
      TXT_SELECTED_COLOR: 'white',
      TXT_COLOR: 'black'
    };

    /*-------------------*/
    /* Scoped functions. */
    /*-------------------*/
    $scope.navigate = navigate;
    $scope.select = select;

    /*----------------------------------------------*/
    /* Watch for changes to the disabled attribute. */
    /*----------------------------------------------*/
    $scope.$watch(
      function () {
        return !!$attrs.disabled;
      },
      function (newValue, oldValue) {
        $scope.disabled = newValue;
      }
    );

    setBorderStyles();

    /*************************************************************************
     * init */
    /**
     * Initialization function to be called by the link method of the directive
     * so the ngModel Controller reference can be passed.
     *
     * @param  ngModelCtrl  Controller object to save.
     *************************************************************************/
    function init(ngModelCtrl) {
      mNgModelCtrl = ngModelCtrl;

      /*----------------------------------------------------*/
      /* Add a format handler for when the ngModel changes. */
      /*----------------------------------------------------*/
      mNgModelCtrl.$formatters.unshift(manualSelection);

      /*--------------------------------------*/
      /* If no values are set on the buttons, */
      /* set them to the index values.        */
      /*--------------------------------------*/
      $scope.buttons.forEach(function (button, index) {
        if (button.value == undefined || button.value == null)
          button.value = index;
      });
    }

    /***************************************************************************
     * manualSelection */
    /**
     * When a manual selection is made from the ngModel, take the value as an
     * array (if it isn't already) and find all matching buttons. For every match
     * found, select that button. If an array is passed in, but multiSelect is
     * not, then the last matching value in the list will be the selected button.
     *
     * @param  ngValue  Incoming ngModel value.
     ***************************************************************************/
    function manualSelection(ngValue) {
      /*----------------------------------------------*/
      /* Make sure that we are working with an array. */
      /*----------------------------------------------*/
      var values = Array.isArray(ngValue) ? ngValue : [ngValue];

      /*-----------------------------------------------------*/
      /* For each selection value, compare with the buttons. */
      /* If a matching value is found, select that button.   */
      /*-----------------------------------------------------*/
      values.forEach(function (value) {
        /*-----------------------------------*/
        /* Select the first matching button. */
        /*-----------------------------------*/
        $scope.buttons.some(function (button) {
          if (button.value === value) return select(button);
        });
      });
    }

    /***************************************************************************
     * navigate */
    /**
     * Gets the direction of navigation from the arrow key that is pressed.
     * Increases or decreases the current index based on that direction and makes
     * sure that index stays within the range.
     *
     * @param  event  Button event.
     ***************************************************************************/
    function navigate(event) {
      /*---------------------------------------------------------*/
      /* If disabled, or an invalid key was pressed, do nothing. */
      /*---------------------------------------------------------*/
      if (
        $scope.multiSelect() ||
        $scope.disabled ||
        NAV_KEYS.indexOf(event.keyCode) < 0
      )
        return;

      /*---------------------------------------------------------*/
      /* Set the direction based on the key pressed. If we are   */
      /* in vertical mode, we only want to allow the up and down */
      /* keys. Otherwise, only allow the left and right keys.    */
      /*---------------------------------------------------------*/
      var direction = $scope.vertical()
        ? event.keyCode == KEYCODES.DOWN_ARROW
          ? 1
          : event.keyCode == KEYCODES.UP_ARROW
          ? -1
          : 0
        : event.keyCode == KEYCODES.RIGHT_ARROW
        ? 1
        : event.keyCode == KEYCODES.LEFT_ARROW
        ? -1
        : 0;

      /*----------------------------------------------------*/
      /* Set the index from the direction, while keeping    */
      /* it within the bounds of the array size of buttons. */
      /*----------------------------------------------------*/
      selectedIndex = Math.max(
        0,
        Math.min($scope.buttons.length - 1, selectedIndex + direction)
      );
      select($scope.buttons[selectedIndex]);
    }

    /***************************************************************************
     * select */
    /**
     * Selects the provided button, or toggles the provided button if multiSelect
     * is true. If this is not for multiSelect, save the previously selected
     * button before ensuring all other buttons have been unselected.
     * Save the index of the button from the list that matches the button we are
     * selecting. This will allow us to traverse through the list of buttons when
     * the arrow keys are used.
     * Use the buttonChanged function from the directive to send the new button
     * and old button back to the use case.
     *
     * @param  button  Button selected.
     * @return         Returns the selected button's value. If multiSelect is
     *                 enabled, this will return an array of all selected
     *                 buttons' values.
     ***************************************************************************/
    function select(button) {
      var oldButton = null;

      /*----------------------------------------------------------*/
      /* If only one button can be selected, unselect the others. */
      /*----------------------------------------------------------*/
      if (!$scope.multiSelect()) {
        $scope.buttons.forEach(function (btn, index) {
          if (btn.selected) oldButton = btn;
          if (btn == button) selectedIndex = index;

          btn.selected = false;
        });

        button.selected = true;
      } else button.selected = !button.selected;

      /*------------------------------------------------*/
      /* Otherwise, toggle the selection of the button. */
      /*------------------------------------------------*/

      /*------------------------------------------------------------*/
      /* Get the selected button(s) and convert to just the values. */
      /*------------------------------------------------------------*/
      var selectedValues = $scope.buttons
        .filter(function (button) {
          return button.selected;
        })
        .map(function (button) {
          return button.value;
        });

      /*----------------------------------------*/
      /* Set the selected value to the ngModel. */
      /*----------------------------------------*/
      mNgModelCtrl.$setViewValue(
        $scope.multiSelect() ? selectedValues : selectedValues[0]
      );

      /*---------------------------------------------------------*/
      /* If not using multi-select, and a buttonChanged function */
      /* was provided, send the previously selected button, and  */
      /* the newly selected button back to the use case.         */
      /*---------------------------------------------------------*/
      if (!$scope.multiSelect() && $scope.buttonChanged)
        $scope.buttonChanged({ $oldButton: oldButton, $newButton: button });
    }

    /***************************************************************************
     * setBorderStyles */
    /**
     * Creates a style element and appends it to the head element. This will add
     * border colors to the buttons depending on the direction of the btnGroup.
     * The buttons seem to be overlapping each other, so to keep the border color
     * visible, both edges (top/bottom or left/right) need to have the border
     * color applied. The edge buttons only need one border colored, so we base
     * the border coloring using the "not" selector.
     ***************************************************************************/
    function setBorderStyles() {
      var style = document.createElement('style');

      style.type = 'text/css';
      if (style.stylesheet) style.styleSheet.cssText = CSS_BUTTON_BORDERS;
      else style.appendChild(document.createTextNode(CSS_BUTTON_BORDERS));

      $element.append($compile(style)($scope));
    }
  }

  /*****************************************************************************
   * Directive Object
   *****************************************************************************/
  return {
    restrict: 'E',
    require: ['ngModel', 'toggleButtons'],
    scope: {
      buttonBorderColor: '@',
      buttonChanged: '&',
      buttons: '=',
      multiSelect: '&',
      vertical: '&'
    },
    template: HTML_BUTTON_TEMPLATE,
    link: function (element, scope, attr, ctrl) {
      ctrl[1].init(ctrl[0]);
    },
    controllerAs: 'ctrl',
    controller: ToggleButtons
  };
}
